import React from 'react';
import clsx from 'clsx';

/**
 * Card component - pure functional component with Tailwind
 * Composable card with header, body, and footer slots
 */
export const Card = React.forwardRef(({
  children,
  className = '',
  variant = 'default',
  padding = 'normal',
  hover = false,
  isFocused = false,
  onClick
}, ref) => {
  const variants = {
    default: 'bg-base-200',
    defaultElevated: 'bg-base-200 shadow-md',
    elevated: 'bg-base-100 shadow-lg',
    ghost: 'bg-transparent border-base-200',
    primary: 'bg-primary/10 border-primary/20',
    blur: 'bg-base-200/50 backdrop-blur-lg shadow-lg',
    // Frosted-glass surface for hover popups/tooltips (see .frosted-popup in app.css)
    frosted: 'frosted-popup',
  };

  const paddingClasses = {
    none: '',
    small: 'p-4',
    normal: 'p-6',
    large: 'p-8'
  };

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={clsx(
        'rounded-lg transition-all',
        variants[variant],
        paddingClasses[padding],
        // Only apply hover styles when not keyboard focused
        hover && !isFocused && 'hover:shadow-md hover:scale-[1.01] cursor-pointer',
        // Keyboard focus indicator (same as StoryCard pattern)
        isFocused && 'ring-4 ring-primary ring-offset-2 ring-offset-base-100 shadow-lg scale-[1.01]',
        className
      )}
    >
      {children}
    </div>
  );
});

export function CardHeader({ children, className = '' }) {
  return (
    <div className={clsx(
      'pb-4 mb-4 border-b border-base-200',
      className
    )}>
      {children}
    </div>
  );
}

export const CardBody = React.forwardRef(({ children, className = '', ...props }, ref) => {
  return (
    <div ref={ref} className={clsx('', className)} {...props}>
      {children}
    </div>
  );
});

export function CardFooter({ children, className = '' }) {
  return (
    <div className={clsx(
      'pt-4 mt-4 border-t border-base-200',
      className
    )}>
      {children}
    </div>
  );
}
